import {
  ApprovalInboxTransaction,
  UserRolesResponse,
  TransactionTypesResponse,
  AccountPortalTransactionType,
} from 'vopay';
import { JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import useFetchUserRoles from '../hooks/useApi/useFetchUserRoles';
import useFetchApprovalTransactionsInbox from '../hooks/useApi/useFetchApprovalTransactionsInbox';
import useFetchApprovalTransactions from '../hooks/useApi/useFetchApprovalTransactions';
import useFetchWorkflows from '../hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '../hooks/useApi/useFetchTransactionTypes';
import useTransactionApprovalAccess from '../hooks/useTransactionApprovalAccess';
import { useClientData } from './ClientData.provider';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type ApprovalWorkflow = {
  allApprovalTransactions: ApprovalInboxTransaction[];
  hasTransactionsRequiringApproval: boolean;
  roles: string[];
  isApprover: boolean;
  hasInvalidApprover: boolean;
  hasInvalidWorkflows: boolean;
  usersReset: () => void;
  transactionsReset: () => void;
  approvalListReset: () => void;
  areEnoughApprovers: (
    minimumAmount?: number,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: UserRolesResponse,
  ) => boolean;
  getPaymentRail: (
    transactionTypeID: AccountPortalTransactionType['ID'],
    transactionTypes?: TransactionTypesResponse,
  ) => string[];
};

export const ApprovalWorkflowContext = createContext<ApprovalWorkflow>({
  allApprovalTransactions: [],
  hasTransactionsRequiringApproval: false,
  roles: [],
  isApprover: false,
  hasInvalidApprover: false,
  hasInvalidWorkflows: false,
  usersReset: () => {},
  transactionsReset: () => {},
  approvalListReset: () => {},
  areEnoughApprovers: () => false,
  getPaymentRail: () => [],
});

export const ApprovalWorkflowProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { response: userRolesResponse, fetchUserRoles: getUserRoles } = useFetchUserRoles();
  const [approvalTransactionsList, getApprovalTransactions] = useFetchApprovalTransactionsInbox({});
  const [allApprovalTransactionsList, getAllApprovalTransactions] = useFetchApprovalTransactions({});
  const [, fetchTransactionTypes] = useFetchTransactionTypes({ isBackground: true });
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const { response } = useClientData();

  const [allApprovalTransactions, setAllApprovalTransactions] = useState<ApprovalInboxTransaction[]>([]);
  const [shouldFetchUserRoles, setShouldFetchUserRoles] = useState(false);
  const [shouldFetchApprovalTransactions, setShouldFetchApprovalTransactions] = useState(false);
  const [shouldFetchAllApprovalTransactions, setShouldFetchAllApprovalTransactions] = useState(false);
  const [shouldFetchWorkflows, setShouldFetchWorkflows] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [hasTransactionsRequiringApproval, setHasTransactionsRequiringApproval] = useState(false);
  const [hasInvalidWorkflows, setHasInvalidWorkflows] = useState(false);
  const [hasInvalidApprover, setHasInvalidApprover] = useState(false);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();

  const usersReset = () => {
    setShouldFetchUserRoles(true);
  };

  const transactionsReset = () => {
    setShouldFetchApprovalTransactions(true);
  };

  const timeDifference = moment().subtract(2, 'year').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');

  const transactionsPeriod = {
    FromDate: timeDifference,
    ToDate: currentDateTime,
  };

  const canUseTransactionApproval = useTransactionApprovalAccess();

  const approvalListReset = () => {
    setShouldFetchAllApprovalTransactions(true);
    setShouldFetchWorkflows(true);
  };

  // Only fetch approval data for accounts entitled to Transaction Approval. Otherwise the backend
  // responds "This operation is not currently enabled for your account" and pollutes Datadog RUM.
  // Re-runs once permissions/client data resolve, so entitled accounts still fetch correctly.
  useEffect(() => {
    if (response.Success && canUseTransactionApproval) {
      setShouldFetchWorkflows(true);
      setShouldFetchAllApprovalTransactions(true);
      setShouldFetchApprovalTransactions(true);
      setShouldFetchUserRoles(true);
    }
  }, [response.Success, canUseTransactionApproval]);

  useEffect(() => {
    if (shouldFetchAllApprovalTransactions) {
      getAllApprovalTransactions({
        StartDateTime: moment(transactionsPeriod.FromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDateTime: moment(transactionsPeriod.ToDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });

      setShouldFetchAllApprovalTransactions(false);
    }
  }, [shouldFetchAllApprovalTransactions]);

  useEffect(() => {
    if (shouldFetchApprovalTransactions) {
      getApprovalTransactions({});
      setShouldFetchApprovalTransactions(false);
    }
  }, [shouldFetchApprovalTransactions]);

  // usersReset() (called from ClientLogin.provider whenever permissions load) also flips
  // isGetUserRoles, so gate the fetch itself — not just the mount effect — on entitlement.
  useEffect(() => {
    if (shouldFetchUserRoles && canUseTransactionApproval) {
      getUserRoles({
        Role: 'approver',
      });

      setShouldFetchUserRoles(false);
    }
  }, [shouldFetchUserRoles, canUseTransactionApproval]);

  useEffect(() => {
    if (shouldFetchWorkflows) {
      refreshWorkflows();

      fetchTransactionTypes({
        CountriesID: response.Country === 'CA' ? 1 : 2,
        IsTransactionApprovalSupported: 1,
      });

      setShouldFetchWorkflows(false);
    }
  }, [shouldFetchWorkflows]);

  useEffect(() => {
    if (userRolesResponse && userRolesResponse.Success) {
      setRoles(userRolesResponse.Roles);
      setIsApprover(Object.values(userRolesResponse.Roles).includes('approver'));
    }
  }, [userRolesResponse]);

  useEffect(() => {
    if (approvalTransactionsList && approvalTransactionsList.Success) {
      setHasTransactionsRequiringApproval(
        Object.values(approvalTransactionsList.Transactions).some((transaction) => !transaction.Approved),
      );
    }
  }, [approvalTransactionsList]);

  useEffect(() => {
    if (allApprovalTransactionsList && allApprovalTransactionsList.Success) {
      setHasInvalidApprover(
        Object.values(allApprovalTransactionsList.Transactions).some(
          (transaction) => transaction.NumberApprovalsRequired > transaction.AvailableApprovers,
        ),
      );

      setAllApprovalTransactions(Object.values(allApprovalTransactionsList.Transactions));
    }
  }, [allApprovalTransactionsList]);

  useEffect(() => {
    const requiredApprovers = workflows.flatMap((group) =>
      group.Workflows.map((workflow) =>
        areEnoughApprovers(
          workflow.MinAmount,
          [group.TransactionType],
          workflow.NumberOfApprovalsRequired,
          userRolesResponse,
        ),
      ),
    );

    setHasInvalidWorkflows(requiredApprovers.includes(false));
  }, [workflows, userRolesResponse]);

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  return (
    <ApprovalWorkflowContext.Provider
      value={{
        allApprovalTransactions,
        roles,
        hasTransactionsRequiringApproval,
        isApprover,
        hasInvalidApprover,
        hasInvalidWorkflows,
        usersReset,
        approvalListReset,
        transactionsReset,
        areEnoughApprovers,
        getPaymentRail,
      }}
    >
      {children}
    </ApprovalWorkflowContext.Provider>
  );

  function areEnoughApprovers(
    minimumAmountProp?: number | string,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: UserRolesResponse,
  ) {
    if (!userRoles?.UserRolePermissions) return true;

    if (minimumAmountProp === undefined) return true;

    if (!requiredApprovers) return true;

    if (!transactionTypes) return true;

    if (transactionTypes.length === 0) return true;

    const roles = Object.values(userRoles.UserRolePermissions);
    const minimumAmount = Number(minimumAmountProp);

    for (const transactionType of transactionTypes) {
      const rolesForThisType = roles
        .filter((userRole) => {
          return userRole.TransactionType === transactionType;
        })
        .filter((userRole) => {
          return (
            userRole.MaxAmount === undefined ||
            userRole.MaxAmount === null ||
            Number(userRole.MaxAmount) >= minimumAmount
          );
        });

      if (rolesForThisType.length === 0) return false;

      if (rolesForThisType.length < Number(requiredApprovers)) return false;
    }

    return true;
  }

  function getPaymentRail(
    transactionTypeID: AccountPortalTransactionType['ID'],
    transactionTypes: TransactionTypesResponse | undefined,
  ): string[] {
    const transactionType = Object.values(transactionTypes?.TransactionTypes || {}).find(
      (transactionTypesItem) => transactionTypesItem.ID === transactionTypeID,
    );

    return transactionType?.TransactionType.split('|') || [];
  }
};

export const useApprovalWorkflow = (): ApprovalWorkflow => useContext(ApprovalWorkflowContext);
